import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Col, Input, Row } from 'antd';
import { TProduct } from 'types/product';

import { ActionsMenu } from '@atoms/ActionsMenu';
import { SkeletonWrapper } from '@common/SkeletonWrapper/SkeletonWrapper';
import { useFetchDataQuery } from '@hooks/useFetchDataQuery';
import { useBatchImages } from '@hooks/useImageBatch';
import { Mode, useMode } from '@hooks/useMode';
import { useMutationData } from '@hooks/useMutationData';
import { ParentBrigadePicker } from '@organisms/brigade/modals/parentBrigadePicker';
import { ImgUploader } from '@organisms/imgUploader/imgUploader';
import { capitalizeFirstLetter } from '@utils/capitalizeFirstLetter';
import { imagePicker } from '@utils/imagePicker';

const { TextArea } = Input;

export const StorageProfile = () => {
    const { storageID, crmId } = useParams();
    const navigate = useNavigate();
    const [storage, setStorage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [progress, setProgress] = useState<{ percentage: number; file: File } | null>(null);

    const mode = useMode();
    const editing = mode !== Mode.VIEW;
    const creating = mode === Mode.CREATE;

    const { addImages, removeImages } = useBatchImages((percentage, file) => {
        setProgress({ percentage, file });
    });

    const { isLoading } = useFetchDataQuery({
        getFnKey: 'getWarehouse',
        params: {
            id: +storageID,
            crm_id: +crmId,
        },
        setDataList(data) {
            setStorage(data);
        },
        enabled: !creating,
    });

    const { mutate: updateWarehouse } = useMutationData({
        mutateFnKey: 'updateWarehouse',
        onSuccess(data) {
            navigate(`/lk/worker/crm/${crmId}/storages/${data.id}`);
        },
    });

    const { mutate: createWarehouse } = useMutationData({
        mutateFnKey: 'createWarehouse',
        onSuccess(data) {
            navigate(`/lk/worker/crm/${crmId}/storages/${data.id}/products`);
        },
    });

    const doEdit = () => {
        navigate('edit');
    };
    const doCancel = () => {
        if (storageID) {
            navigate(-1);
        } else {
            navigate('../');
        }
    };

    const doSave = () => {
        if (creating) {
            createWarehouse({ ...storage, crm_id: crmId });
        } else {
            updateWarehouse(storage);
        }
    };

    const handeSetBrigade = (brigade) => {
        setStorage((prev) => ({
            ...prev,
            brigade: brigade,
            brigade_id: brigade?.id,
        }));
    };

    async function handleImageSelect(file: File): Promise<void> {
        setStorage((prevState) => ({
            ...prevState!,
            pictures: [...(prevState?.pictures as File[]), file],
        }));

        const uploaded = (await addImages('warehouses', +storageID, [file])) as unknown as TProduct;
        const newPictures = uploaded?.pictures || [];

        setStorage((prevState) => ({
            ...prevState!,
            pictures: [...newPictures],
        }));
    }

    async function handleImageDelete(index: number): Promise<void> {
        const pictureId = (storage.pictures as any[])[index]?.id;
        if (!pictureId) return;

        await removeImages('warehouses', +storageID, [pictureId]);

        setStorage((prevState) => ({
            ...prevState!,
            pictures: (prevState?.pictures as any[]).filter((_, i) => i !== index),
        }));
    }

    return (
        <div id="app-product-category-profile">
            <div className="product-category-profile">
                <SkeletonWrapper isLoading={isLoading}>
                    <Row className="product-category-info">
                        <Col className="avatar outer-box">
                            <ImgUploader
                                picture={imagePicker({ picture: storage?.picture })}
                                onImageSelect={handleImageSelect}
                                isEdit={editing}
                                onImageDelete={handleImageDelete}
                                progress={progress}
                            />
                        </Col>
                        <Col className="outer-box">
                            <Row className="label-row">
                                <Col>
                                    <p>Название</p>
                                </Col>
                            </Row>
                            <Row className="value-row">
                                <Col>
                                    {editing ? (
                                        <TextArea
                                            autoSize={{ minRows: 2, maxRows: 5 }}
                                            value={storage?.name}
                                            onChange={(e) => {
                                                setStorage((prev) => ({
                                                    ...prev,
                                                    name: capitalizeFirstLetter(e.target.value),
                                                }));
                                            }}
                                        />
                                    ) : (
                                        <p>{storage?.name}</p>
                                    )}
                                </Col>
                            </Row>
                        </Col>
                        <Col className="outer-box">
                            <Row className="label-row">
                                <Col>
                                    <p>Ответственный</p>
                                </Col>
                            </Row>
                            <Row
                                className="value-row"
                                onClick={() => {
                                    if (editing) setIsModalOpen(true);
                                }}
                            >
                                <Col>
                                    {editing ? (
                                        <a>{storage?.brigade_id ?? 'Не выбрана'}</a>
                                    ) : (
                                        <p>{storage?.brigade_id ?? 'Не выбрана'}</p>
                                    )}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </SkeletonWrapper>
            </div>
            <ParentBrigadePicker
                currentBrigade={storage?.brigade}
                setCurrentBrigade={handeSetBrigade}
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
            />
            <ActionsMenu
                doSave={editing ? doSave : null}
                doCancel={editing ? doCancel : null}
                doEdit={!editing ? doEdit : null}
            />
        </div>
    );
};
